import { ai_api } from "../utils/variables"

// Store Validator lives on the "ai" backend (ai.keyquest.io / qa.ai.keyquest.io),
// not the main api.keyquest.io host. `ai_api` already points at the right host,
// driven by REACT_APP_PROD_AI_APIKEY (scoped per environment in Vercel).
const BASE = `${ai_api}/store-validator/api`

const authHeaders = (extra = {}) => ({
  Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
  ...extra,
})

async function handleResponse(response) {
  const text = await response.text()
  let data = null
  try {
    data = text ? JSON.parse(text) : null
  } catch (_) {
    data = text
  }
  if (!response.ok) {
    const err = new Error(data?.message || data?.detail || data?.error || `HTTP ${response.status}`)
    err.status = response.status
    err.data = data
    throw err
  }
  return data
}

const jsonGet = async path => handleResponse(await fetch(`${BASE}${path}`, { method: "GET", headers: authHeaders() }))

const jsonPost = async (path, body) =>
  handleResponse(
    await fetch(`${BASE}${path}`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(body),
    })
  )

const jsonDelete = async path => handleResponse(await fetch(`${BASE}${path}`, { method: "DELETE", headers: authHeaders() }))

// Don't set Content-Type for multipart — the browser needs to add its own boundary.
const formPost = async (path, formData) =>
  handleResponse(await fetch(`${BASE}${path}`, { method: "POST", headers: authHeaders(), body: formData }))

// For endpoints that return a real binary file (a zip, here) instead of the {...} JSON shape
// every other endpoint in this client returns — handleResponse's JSON.parse would corrupt it.
const blobGet = async path => {
  const response = await fetch(`${BASE}${path}`, { method: "GET", headers: authHeaders() })
  if (!response.ok) {
    const text = await response.text().catch(() => "")
    const err = new Error(text || `HTTP ${response.status}`)
    err.status = response.status
    throw err
  }
  return response.blob()
}

const appendFormEntries = (fd, obj) => {
  Object.entries(obj || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return
    if (Array.isArray(value)) {
      value.forEach(v => v !== undefined && v !== null && fd.append(key, v))
    } else {
      fd.append(key, value)
    }
  })
}

export const storeValidatorApi = {
  // ── app lifecycle ──────────────────────────────────────────────────────────
  getPlatforms: () => jsonGet("/platforms/"),
  createAppId: platform => jsonPost("/app-id/", { platform }),
  getUserApps: () => jsonGet("/apps/"),
  getAppHistory: appId => jsonGet(`/history/${appId}/`),
  // Read-only, same response shape as getAppHistory — works for ANY authenticated user (not
  // just the owner) once status is "in_review" (403 before that).
  getSharedApp: appId => jsonGet(`/shared/${appId}/`),
  deleteApp: appId => jsonDelete(`/history/${appId}/`),
  // Returns a real .zip file (all of this app's details) — a Blob, not JSON.
  exportApp: appId => blobGet(`/export/${appId}/`),
  // Predicted IARC rating certificate (2026-10 feature) — a real PDF, not JSON. 400s with
  // {error: "..."} if this app has no saved IARC questionnaire answers yet (blobGet's error
  // path hands back that raw JSON text as err.message — the caller parses it for a nicer message).
  getIarcCertificate: appId => blobGet(`/iarc/certificate/${appId}/`),

  // Lightweight partial save — accepts app_id/platform plus whatever fields are ready.
  // This is the real "Save draft" endpoint (separate from the full Complete App Validate).
  saveDraft: (appId, platform, fields) => jsonPost("/draft/", { app_id: appId, platform, ...fields }),

  // Marks an app's review status. Confirmed shape (2026-08-26): no platform in the body, just
  // app_id + status. Was "in_review" (2026-08-26 doc) — the backend now rejects that with
  // {"error": "Unsupported status 'in_review' -- only 'ready_to_publish' is supported."}, so
  // this sends "ready_to_publish" instead (2026-10 confirmed live).
  submitForReview: (appId, status = "ready_to_publish") => jsonPost("/apps/", { app_id: appId, status }),

  // ── per-field validate / correct — both require app_id — title, short_description,
  //    long_description, keywords, category, art_style, game_theme, gameplay_challenge,
  //    gameplay_mechanics, genre ─────────────────────────────────────────────
  validateField: (fieldKey, platform, text, appId) => jsonPost(`/validate/${fieldKey}/`, { app_id: appId, platform, text }),
  correctField: (fieldKey, platform, text, appId) => jsonPost(`/correct/${fieldKey}/`, { app_id: appId, platform, text }),

  // (2026-09 doc) Every one of the 21 deterministic format fields (specs/details/content_rating)
  // now has its own single-field validate endpoint too — same URL pattern as validateField, but
  // the payload key is the field's own name, not `text`. Optional+blank returns
  // {skipped: true} instead of erroring or overwriting a previously-good stored result.
  // Correct/ isn't offered for these — only the 10 text/choice fields above support it.
  validateFormatField: (fieldKey, platform, value, appId) =>
    jsonPost(`/validate/${fieldKey}/`, { app_id: appId, platform, [fieldKey]: value }),

  // (Confirmed doc, 2026-09) Every one of the 10 text/choice fields AND all 21 format fields
  // also has its own single-field GENERATE endpoint — same URL pattern as validate/correct.
  // `brief` optional: omitted, falls back to this app_id's saved context (earlier-pipeline
  // groups + this group's own saved fields), or invents a direction if nothing's saved either.
  // Response shape differs by field kind: a text/choice field returns `text`; a format field
  // returns `value` (plus `is_example: true` when it's an invented placeholder like a fake
  // developer name/email/URL that must be replaced, not submitted as-is). Synchronous — no
  // 202/processing, unlike whole-app or asset-image generation.
  generateField: (fieldKey, platform, appId, brief) =>
    jsonPost(`/generate/${fieldKey}/`, { platform, app_id: appId, ...(brief ? { brief } : {}) }),

  // (2026-09 doc) Each of the 7 asset keys also has its own single-file validate endpoint —
  // scores just that key, lighter than resending every asset via validateAssetsGroup.
  // `fileOrFiles` may be a single File (most keys) or an array (screenshots).
  validateAssetKey: (assetKey, platform, appId, fileOrFiles) => {
    const fd = new FormData()
    fd.append("app_id", appId)
    fd.append("platform", platform)
    ;(Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles]).forEach(f => f && fd.append(assetKey, f))
    return formPost(`/validate/${assetKey}/`, fd)
  },

  // ── section-scoped validate/generate (2026-08-26) — one per tab: name, categorization,
  //    specs, details, assets, content_rating. Much lighter than the full Complete App
  //    Validate/Generate calls since they only touch that section's own fields. ─────────
  validateGroup: (group, appId, platform, fields) => jsonPost(`/validate/group/${group}/`, { app_id: appId, platform, ...fields }),
  validateAssetsGroup: (appId, platform, files) => {
    const fd = new FormData()
    fd.append("app_id", appId)
    fd.append("platform", platform)
    appendFormEntries(fd, files)
    return formPost("/validate/group/assets/", fd)
  },
  // Per-section "Regenerate" (2026-09 doc) — lighter than the whole-app generate/complete/
  // flow when only one section needs a redo. `brief` is optional: omitted, the API falls back
  // to this app_id's already-saved context (other groups' saved fields), or invents a
  // direction from nothing if there's no context either. Response already includes fresh
  // scores for the fields it generated (res.sections), same shape as validate/group/<group>/.
  // `poll` re-checks an in-flight generate the same way asset generation's own poll flag does
  // (see generateAssetKey/generateAssetsGroup below) — content_rating's own generate now also
  // drives the IARC questionnaire's AI fill, a heavier task than this group's other fields, so
  // it's defensively supported here too even though most groups likely never actually go async.
  generateGroup: (group, platform, appId, brief, poll) =>
    jsonPost(`/generate/group/${group}/`, { platform, app_id: appId, ...(brief ? { brief } : {}), ...(poll ? { poll: true } : {}) }),

  // AI-fills the IARC content-rating questionnaire from this app_id's saved context — no brief
  // param shown in the confirmed request shape. Still 404ing server-side ("Unknown section") as
  // of 2026-10, so whether this goes async is unconfirmed either way — `poll` is supported
  // defensively, same shape as every other generate endpoint's poll flag, for whenever it's fixed.
  generateIarcQuestionnaire: (platform, appId, poll) =>
    jsonPost("/generate/iarc_questionnaire/", { platform, app_id: appId, ...(poll ? { poll: true } : {}) }),

  // Scores the IARC questionnaire's current answers (confirmed request/response shape, 2026-10):
  // `answers` is { question_id: response_id } for a radio question, { question_id: [response_id,
  // ...] } for a checkbox question — only currently-shown (unlocked) questions. Response is a
  // single score/verdict/reason (same shape every other single-field validate call returns),
  // plus the full annotated question tree (reachable/status/answer per question) and an `answers`
  // echo — this call only needs the score/verdict/reason part.
  validateIarcQuestionnaire: (platform, appId, answers) =>
    jsonPost("/validate/iarc_questionnaire/", { platform, app_id: appId, answers }),

  // ── conversational full-app generate (2026-09) ─────────────────────────────
  // /generate/complete/ asks clarifying follow-up questions before generating the whole app
  // (including real AI-generated asset images) in one shot. Confirmed shape (2026-09-09):
  // `brief` and `answers` are mutually exclusive — the API rejects a request containing both.
  // Start/restart with generateComplete (brief only); once a response comes back with
  // `status: "needs_clarification"` and a `questions` array, answer that round with
  // continueGenerateComplete — a flat array of answer strings in the same order as the
  // questions it asked, no brief. Repeat until the response carries `groups` + `overall`.
  generateComplete: (platform, appId, brief) => jsonPost("/generate/complete/", { platform, app_id: appId, brief }),
  continueGenerateComplete: (platform, appId, answers) => jsonPost("/generate/complete/", { platform, app_id: appId, answers }),
  // Per the backend engineer directly (2026-09-11), poll by re-POSTing /generate/complete/
  // with just platform + app_id (no brief, no answers) once generation is underway, until
  // status moves past "processing" — this is what's actually implemented, superseding the
  // separate GET /generate/complete/status/<app_id>/ shown in the written doc.
  getGenerateCompleteStatus: (platform, appId) => jsonPost("/generate/complete/", { platform, app_id: appId }),

  // ── asset image generation (2026-09 doc) — real billed OpenAI image calls, async when
  //    app_id is given: both return 202 {"status": "processing"}. Confirmed live (2026-09-16):
  //    there's no separate status endpoint in use — poll by re-POSTing the very same call (asset
  //    key or the whole group) with just platform + app_id, same self-poll pattern as
  //    generateComplete/getGenerateCompleteStatus above. Confirmed live (2026-09-17): the poll
  //    call must also carry `poll: true` (e.g. { app_id, platform, poll: true }) so the backend
  //    can tell a status check apart from a real duplicate generation attempt on the same
  //    app_id, instead of 409ing it as "already in progress". `brief` is optional on the
  //    starting call — omitted, the API falls back to this app_id's saved listing, or invents a
  //    direction from nothing; `brief` and `poll` are mutually exclusive, same shape as
  //    generateComplete's brief/answers split. ────────
  generateAssetKey: (assetKey, platform, appId, brief, poll) =>
    jsonPost(`/generate/${assetKey}/`, { platform, app_id: appId, ...(brief ? { brief } : {}), ...(poll ? { poll: true } : {}) }),
  generateAssetsGroup: (platform, appId, brief, poll) =>
    jsonPost("/generate/group/assets/", { platform, app_id: appId, ...(brief ? { brief } : {}), ...(poll ? { poll: true } : {}) }),

  // ── assets (multipart, now requires app_id) ────────────────────────────────
  validateAssets: (appId, platform, files) => {
    const fd = new FormData()
    fd.append("app_id", appId)
    fd.append("platform", platform)
    appendFormEntries(fd, files)
    return formPost("/validate/assets/", fd)
  },

  // ── full app validate/save (multipart: text fields + files together) ──────
  completeAppValidate: (fields, files) => {
    const fd = new FormData()
    appendFormEntries(fd, fields)
    appendFormEntries(fd, files)
    return formPost("/validate/", fd)
  },

  // ── translate / generate ───────────────────────────────────────────────────
  // Confirmed shape (2026-08-26): translate now operates on the app's own saved content via
  // app_id, not an arbitrary `text` param.
  translate: (appId, platform, targetLanguages) => jsonPost("/translate/", { app_id: appId, platform, target_languages: targetLanguages }),
  generateApp: (platform, brief, appId) => jsonPost("/generate/", { platform, brief, app_id: appId }),
}

// Maps UI form field id -> API validate/correct path segment, for the fields
// that have a dedicated endpoint. Fields not listed here (specs, details,
// assets, content rating, genre) fall back to existing behavior until their
// integration is confirmed.
export const STORE_VALIDATOR_FIELD_ENDPOINTS = {
  appName: "title",
  shortDescription: "short_description",
  longDescription: "long_description",
  keywords: "keywords",
  category: "category",
  artStyle: "art_style",
  gameTheme: "game_theme",
  gameplayChallenge: "gameplay_challenge",
  gameplayMechanics: "gameplay_mechanics",
  // primaryGenre / additionalGenre1 / additionalGenre2 omitted from this map — genre has its
  // own dedicated validate/genre/ and correct/genre/ endpoints, but the UI's 3 genre dropdowns
  // are joined into one comma-separated string first; StoreValidator.jsx handles that specially
  // (GENRE_FIELD_IDS branch) rather than going through this generic map.
}

export default storeValidatorApi
